<template>
  <div class="mpf-grid-row">
    <!-- Full width, not two-thirds: the slot grid earns the extra columns, and
         the pagination should reach the content edge rather than stop short. -->
    <div class="mpf-grid-column-full">
      <MpfNotificationBanner
        v-if="slotTakenMessage"
        variants="error"
        heading="That time is no longer available"
        :additional-text="slotTakenMessage"
      />

      <MpfDetails
        v-if="hasFilters"
        id="appointment-filters"
        summary="Filter appointments"
        :open="filtersOpen"
      >
        <MpfCheckboxes
          v-model="genderFilter"
          legend="Practitioner gender"
          label="Practitioner gender"
          hint="Leave both unticked if you have no preference."
          name="preferredPractitionerGenders"
          :items="genderItems"
          @change="onGenderChange"
        />

        <!-- Practitioners arrive with each slots response and are per-week, so
             this list changes as the patient pages. -->
        <MpfSelect
          v-if="practitioners.length"
          v-model="practitionerFilter"
          label="Practitioner"
          name="preferredPractitioner"
          :items="practitionerItems"
          :include-empty-option="false"
          @change="onPractitionerChange"
        />

        <!-- A pinned site comes back as a one-element list with no "Any", so
             there is nothing to choose and the select is not offered. -->
        <MpfSelect
          v-if="showSiteFilter"
          v-model="siteFilter"
          label="Location"
          name="preferredSite"
          :items="siteItems"
          :include-empty-option="false"
          @change="onSiteChange"
        />

        <MpfButton
          v-if="filtersApplied"
          label="Reset"
          variant="secondary"
          @click="onResetFilters"
        />
      </MpfDetails>

      <MpfSlotPicker
        :weeks="weeks"
        :selected-slot-id="selectedSlotId"
        :loading="apiLoading"
        :empty-week-message="emptyWeekMessage"
        @select="onSelectSlot"
      />

      <MpfPagination
        v-if="hasPaging"
        :has-previous="previousFromDate !== null"
        :has-next="nextFromDate !== null"
        @previous="store.previousWeeks()"
        @next="store.nextWeeks()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useRouter } from 'vue-router';

  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import MpfCheckboxes from '../components/MpfCheckboxes/MpfCheckboxes.vue';
  import MpfDetails from '../components/MpfDetails/MpfDetails.vue';
  import MpfNotificationBanner from '../components/MpfNotificationBanner/MpfNotificationBanner.vue';
  import MpfPagination from '../components/MpfPagination/MpfPagination.vue';
  import MpfSelect from '../components/MpfSelect/MpfSelect.vue';
  import MpfSlotPicker from '../components/MpfSlotPicker/MpfSlotPicker.vue';
  import { useAppointmentBookingStore } from '../../stores/useAppointmentBookingStore';
  import { useErrorStore } from '../../stores/useErrorStore';
  import { useBookingLinkRoute } from '../composables/useBookingLinkRoute';
  import type { PractitionerGender } from '../../types/appointment';

  const router = useRouter();
  const store = useAppointmentBookingStore();
  const errorStore = useErrorStore();
  const { bookingLinkId, pathForStep } = useBookingLinkRoute();

  const {
    apiLoading,
    filtersApplied,
    hasFilters,
    hasMultipleSites,
    siteRestricted,
    sites,
    nextFromDate,
    practitioners,
    previousFromDate,
    selectedSlotId,
    weeks,
  } = storeToRefs(store);

  const genderFilter = ref<(string | number | null)[]>([]);
  const practitionerFilter = ref<string | null>(null);
  const siteFilter = ref<string | null>(null);
  const slotTakenMessage = ref<string | null>(null);

  const genderItems = [
    { value: 'MALE', label: 'Male' },
    { value: 'FEMALE', label: 'Female' },
  ];

  // "Any" is a real option rather than an empty first entry, so the select never
  // reads as unanswered when no preference is exactly what the patient means.
  const practitionerItems = computed(() => [
    { value: '', label: 'Any' },
    ...practitioners.value.map(({ value, label }) => ({ value, label })),
  ]);

  // Neither list carries an "Any" entry, so the view adds one where a choice is
  // genuinely open. A pinned site is a one-element list: nothing to choose.
  const siteItems = computed(() => [
    { value: '', label: 'Any location' },
    ...sites.value.map(({ value, label }) => ({ value, label })),
  ]);

  const showSiteFilter = computed(
    () => !siteRestricted.value && hasMultipleSites.value && sites.value.length > 1,
  );

  // Open on desktop and whenever filters are applied; collapsed on mobile, where
  // an expanded panel would push the slots off the first screen.
  const isDesktop = ref(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 40.0625em)').matches : true,
  );
  const filtersOpen = computed(() => isDesktop.value || filtersApplied.value);

  const hasPaging = computed(() => previousFromDate.value !== null || nextFromDate.value !== null);

  // An empty week under a filter is not the same as no availability at all — the
  // default message would tell the patient to give up when Reset is what they need.
  const emptyWeekMessage = computed(() =>
    filtersApplied.value
      ? 'No appointments match your filters this week. Try resetting them or looking at another week.'
      : 'No appointments available for online booking.',
  );

  onMounted(async () => {
    if (!bookingLinkId.value) return;
    if (!store.linkType) await store.loadLink(bookingLinkId.value);

    // The cookie outlives a refresh where `isValidPatient` does not, so ask the
    // server: only a 401 means the patient must authenticate again.
    const result = await store.loadSlots(null);

    if (!result.ok && result.status === 401) {
      // push, not replace: only push carries the tenant prefix.
      await router.push(pathForStep('appointment-booking-patient-details'));
    }
  });

  const onGenderChange = async (value: (string | number | null)[]) => {
    genderFilter.value = value;
    await store.applyFilters({
      preferredPractitionerGenders: value.filter(Boolean) as PractitionerGender[],
    });
  };

  const onPractitionerChange = async (value: string | number | null) => {
    await store.applyFilters({ preferredPractitioner: (value as string) || null });
  };

  const onSiteChange = async (value: string | number | null) => {
    await store.applyFilters({ preferredSite: (value as string) || null });
  };

  const onResetFilters = async () => {
    genderFilter.value = [];
    practitionerFilter.value = null;
    siteFilter.value = null;
    await store.clearFilters();
  };

  /** Selecting a slot books it; the one recoverable failure keeps the patient here. */
  const onSelectSlot = async (slotId: string) => {
    slotTakenMessage.value = null;

    const result = await store.book(slotId);

    if (!result.ok) {
      errorStore.setErrors(result.errors);
      slotTakenMessage.value =
        errorStore.getFirstMessage() ||
        'That appointment time has just been taken. Please choose another one.';
      await store.loadSlots();
      return;
    }

    await router.push(pathForStep('appointment-booking-booked-successfully'));
  };
</script>

<style lang="scss" scoped>
  // Same separation below the filters as between two weeks, so the panel reads
  // as its own band rather than as part of the first week.
  #appointment-filters {
    margin-bottom: var(--mpf-space-56);
  }
</style>
